import {
  ComponentInternalInstance,
  computed,
  ComputedRef,
  getCurrentInstance,
  reactive,
  Ref,
  ref,
  UnwrapRef,
} from '@vue/composition-api';

import loginWithPhoneHandler from '@/renderer/components/login/compositions/loginWithPhoneHandler';
import user from '@/renderer/scripts/utils/user';
import tips from '@/renderer/scripts/utils/tips';
import errorPhoneTips from '@/renderer/components/login/compositions/errorPhoneTips';
import store from '@/renderer/store';
import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';
import { getDeviceName, getTencentCaptchaLanguage, decodeText } from '@/renderer/scripts/utils/tools';
import { CONSTANT_URL } from '@/renderer/scripts/http';
import i18n, { i18n_t } from '@/locales/i18n';

const defaultCode = '+86';

const errCodeMap: Record<string, string> = {
  FreeOfficialAccountNotAllowedForRegularAccount: i18n_t('当前已有企业版账号，无法创建免费体验版'),
  DuplicateFreeOfficialAccount: i18n_t('当前已有生效免费版账号，无法继续创建'),
  FreeOfficialAccountExpiredAndChangeToOtherChannel: i18n_t('当前免费版已过期，请前往登录切换到其他渠道版'),
  FreeOfficialAccountExpired: i18n_t('当前免费版已过期，无法重新创建'),
  SensitiveOrgNameRecognized: i18n_t('企业名称无效，请重新输入'),
};

const errReporterMap: Record<string, string> = {
  FreeOfficialAccountNotAllowedForRegularAccount: 'fail_business',
  DuplicateFreeOfficialAccount: 'fail_already_free',
  FreeOfficialAccountExpiredAndChangeToOtherChannel: 'fail_expired_free',
  FreeOfficialAccountExpired: 'fail_expired_free',
  FreeOfficialAccountChannelUnavailable: 'end_activity',
  ApplicationFrequencyLimitReached: 'crowd_congestion',
  FreeOfficialAccountQuotaLimitReached: 'no_more_places',
};

type Handler = {
  isStepOne: Ref<boolean>;
  formData: UnwrapRef<any>;
  phoneMaxLen: ComputedRef<number>;
  errMsg: Ref<string>;
  invalidNameMsg: Ref<string>;
  registerLoading: Ref<boolean>;
  isCounting: Ref<boolean>;
  smsText: ComputedRef<string>;
  disabledFormItem: {
    phoneNumber: boolean;
    sms: boolean;
  };
  methods: {
    handleCodeChange: (val: any) => void;
    clickSendSMS: () => void;
    initCaptcha: () => void;
    registerToLogin: () => void;
  };
};

export default function registerHandler(): Handler {
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const isStepOne = ref(true); // 是否在获取验证码页面
  const formData = reactive({
    code: defaultCode,
    phoneNumber: '',
    sms: '',
    business: '',
    orgName: '',
    role: '',
    scale: '',
  });
  const phoneMaxLen = computed(() => (formData.code === '+86' ? 11 : 20));
  // 防水墙
  let captcha: null | any = null;
  const captchaInfo = reactive({
    captchaTicket: '',
    captchaRandstr: '',
  });
  const errMsg = ref('');
  const invalidNameMsg = ref('');
  const registerStep: Ref<'' | 'sendingSms' | 'registering'> = ref('');
  const registerLoading = ref(false);
  const disabledFormItem = reactive({
    phoneNumber: false,
    sms: false,
  });
  const wechatCode = ref(''); // 使用微信扫码登录时用到
  const { isCounting, smsText, getSMS } = loginWithPhoneHandler();
  const methods = {
    init() {
      const bindInfo = vm.$route.query?.bind;
      if (bindInfo) {
        const decodeBindInfo = decodeText(bindInfo as string);
        if (decodeBindInfo) {
          // 填充手机号、验证码
          try {
            const data = JSON.parse(decodeBindInfo);
            Object.assign(formData, data);
            wechatCode.value = data.wechatCode;
            isStepOne.value = false;
            disabledFormItem.phoneNumber = !!data.phoneNumber;
            disabledFormItem.sms = !!data.sms;
          } catch (e) {
            //
          }
        }
      }
    },
    // 手机前缀改变
    handleCodeChange(val: any) {
      user.setCountryCode(val);
      if (formData.phoneNumber && formData.phoneNumber.length > phoneMaxLen.value) {
        formData.phoneNumber = formData.phoneNumber.substring(0, phoneMaxLen.value);
      }
    },
    // 点击发送验证码
    async clickSendSMS() {
      if (!formData.phoneNumber) {
        tips.error(i18n_t('请先输入手机号'));
        return;
      }
      registerStep.value = 'sendingSms';
      const smsResult = await getSMS({
        countryCode: formData.code,
        phoneNumber: formData.phoneNumber as string,
        type: 'register-for-free',
        ...(captchaInfo.captchaTicket && captchaInfo),
      });
      if (smsResult.error) {
        if (['GraphicCaptchaNotFound', 'InvalidGraphicCaptcha'].includes((smsResult.error as any).code)) {
          // 需要图形验证码之后重新发送
          captcha.show();
          // 考虑安全问题，在获取验证码阶段只判断有无注册过免费版
        } else if (
          ['DuplicateFreeOfficialAccount', 'FreeOfficialAccountExpired'].includes((smsResult.error as any).code)
        ) {
          reporter.report('free_trial_version', {
            verification_code: 'fail_already_free',
          });
          errMsg.value = errCodeMap[(smsResult.error as any).code];
        } else if ((smsResult.error as any).code === 'PhoneNumberInSmsBlackList') {
          reporter.report('free_trial_version', { verification_code: 'fail_already_free' });
          errorPhoneTips();
        } else {
          reporter.report('free_trial_version', {
            verification_code: 'fail_already_free',
          });
          tips.error(smsResult.error as MsgError);
        }
      } else {
        reporter.report('free_trial_version', {
          verification_code: 'success',
        });
        if (isStepOne) {
          isStepOne.value = false;
        }
      }
    },
    // 初始化图形验证码
    initCaptcha() {
      try {
        // @ts-ignore
        captcha = new TencentCaptcha(
          '2091142919',
          // @ts-ignore
          res => {
            // 清空
            Object.assign(captchaInfo, {
              captchaTicket: '',
              captchaRandstr: '',
            });
            captcha.destroy();
            // 验证成功
            if (res.ret === 0) {
              Object.assign(captchaInfo, {
                captchaTicket: res.ticket || '',
                captchaRandstr: res.randstr || '',
              });
              if (registerStep.value === 'sendingSms') {
                methods.clickSendSMS();
              } else {
                methods.registerToLogin();
              }
            }
          },
          {
            userLanguage: getTencentCaptchaLanguage(i18n.locale),
          },
        );
      } catch (e) {
        /* i18n-ignore*/
        logger.ui('TencentCaptcha初始化失败');
      }
    },
    // 注册登录
    async registerToLogin() {
      registerStep.value = 'registering';
      errMsg.value = '';
      invalidNameMsg.value = '';
      const registerParams = {
        ...formData,
        countryCode: formData.code,
        phoneNumber: formData.phoneNumber,
        code: formData.sms,
        deviceId: getDeviceName(),
        wechatCode: wechatCode.value,
        domain: CONSTANT_URL.currentDomain,
        ...(captchaInfo.captchaTicket && captchaInfo),
      };
      registerLoading.value = true;
      const res = await store.dispatch('registerWithPhone', registerParams);
      if (res.hasError) {
        tips.closeAll();
        // 需要图形验证码
        if (['GraphicCaptchaNotFound', 'InvalidGraphicCaptcha'].includes(res.code)) {
          captcha.show();
        } else if (
          [
            'FreeOfficialAccountNotAllowedForRegularAccount',
            'DuplicateFreeOfficialAccount',
            'FreeOfficialAccountExpiredAndChangeToOtherChannel',
            'FreeOfficialAccountExpired',
          ].includes(res.code)
        ) {
          reporter.report('free_trial_version', {
            result: `${errReporterMap[res.code]}`,
          });
          errMsg.value = errCodeMap[res.code];
        } else if (['SensitiveOrgNameRecognized'].includes(res.code)) {
          reporter.report('free_trial_version', {
            result: `${errReporterMap[res.code]}`,
          });
          invalidNameMsg.value = errCodeMap[res.code];
        } else if (res.code === 'SmsCodeInvalidOrExpired') {
          // 验证码过期 需要重新发送验证码
          disabledFormItem.sms = false;
          tips.error(res);
        } else if (
          [
            'FreeOfficialAccountChannelUnavailable',
            'ApplicationFrequencyLimitReached',
            'FreeOfficialAccountQuotaLimitReached',
          ].includes(res.code)
        ) {
          reporter.report('free_trial_version', {
            result: `${errReporterMap[res.code]}`,
          });
          tips.error(res, 6000);
          dispatchRouter(
            {
              name: 'login',
            },
            'replace',
          );
        } else {
          tips.error(res);
        }
        registerLoading.value = false;
        return;
      }
      methods.afterRegister(res);
    },
    async afterRegister(res: CoFile.LoginSMSData) {
      // 如果有登录态，先退出登录，避免缓存影响
      if (vm.$store.getters.isLogined) {
        user.signOut(0);
        setTimeout(() => {
          methods.registerProcess(res);
        }, 3000);
      } else {
        // 没有登录态直接执行登录
        methods.registerProcess(res);
      }
    },
    async registerProcess(res: CoFile.LoginSMSData) {
      user.setCountryCode(formData.code);
      user.setPhoneNumber(formData.phoneNumber as string);
      const routerName = store.getters.isMobileStyle ? 'recentUse' : 'workbench';
      if (res.organizations.length !== 0) {
        // 注册登录--验证登录
        await user.verifyAndLogin(res);
        // 注册登录--获取用户信息
        await store.dispatch('getUserBaseInfo');
        tips.success(i18n_t('免费版创建成功，欢迎使用'));
        dispatchRouter({ name: routerName });
        reporter.report('free_trial_version', {
          result: 'success',
          industry: formData.business,
          company_name: formData.orgName,
          position: formData.role,
          scale: formData.scale,
        });
      }
    },
  };

  methods.init();

  return {
    isStepOne,
    formData,
    phoneMaxLen,
    errMsg,
    invalidNameMsg,
    registerLoading,
    isCounting,
    smsText,
    disabledFormItem,
    methods,
  };
}
